// FLAGS: health-data
// Folds a client's medical check-in themes into the single overview row the client page renders. Pure shaping — no
// network call, no cache.
import { useMemo } from "react";
import {
  ModelsTheme,
  ResponseVitalityCheckinVitalityCheckinModuleScoreResponse,
} from "@src/api/v1/data-contracts";
import { QUESTIONS_KEYS } from "@src/constants";
import { useScoredQuestionLists } from "./useScoredQuestionLists";

// FLAGS: health-data
// The row's headline `score` is taken from the *first* entry of `questionsWithAnswers`, which follows the order the API
// happened to return, not the newest check-in. A reordered response changes the score a clinician reads, silently.
export const useMedicalCheckinOverviewData = (
  medicalThemeData: ModelsTheme[] | undefined,
): ResponseVitalityCheckinVitalityCheckinModuleScoreResponse[] => {
  const { scoredQuestionLists } = useScoredQuestionLists(medicalThemeData);

  return useMemo(() => {
    if (
      !medicalThemeData ||
      medicalThemeData.length === 0 ||
      scoredQuestionLists.length === 0
    ) {
      return [];
    }

    const questionsWithAnswers = scoredQuestionLists.map((ql) => ({
      questionKey: QUESTIONS_KEYS.MEDICAL,
      score: ql.score,
      createdAt: ql.createdAt,
      isScored: true,
    }));

    const firstTheme = medicalThemeData[0];

    return [
      {
        createdAt: firstTheme.createdAt ?? "",
        isFocusedTheme: firstTheme.isFocused ?? false,
        key: QUESTIONS_KEYS.MEDICAL,
        themeKey: QUESTIONS_KEYS.MEDICAL,
        questionsWithAnswers,
        score: questionsWithAnswers[0].score,
      },
    ];
  }, [medicalThemeData, scoredQuestionLists]);
};
